import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import R3Chat from '@reachinsight-app/shared/src/components/R3Chat/R3Chat'
import type { R3ChatHandle } from '@reachinsight-app/shared/src/components/R3Chat/R3Chat'
import type { SessionSummary, McpAppData, SiteArtifacts, SiteBuilderPlanData, SiteBuilderCodeChunkData, CrosstabsClassificationData, CrosstabsObjectivesData, CrosstabsPlanData } from '@reachinsight-app/shared/src/components/R3Chat/types'
import McpAppContainer from '@reachinsight-app/shared/src/components/R3Chat/McpAppContainer'
import type { McpAppContainerHandle } from '@reachinsight-app/shared/src/components/R3Chat/McpAppContainer'
import { SessionsOverlay } from './SessionsOverlay'
import { AgentsScreen } from './AgentsScreen'
import { WebChatService } from '@reachinsight-app/shared/src/services/WebChatService'
import { useFeatureFlag, usePostHog } from '@reachinsight-app/shared/src'
import { useAuth } from '../hooks/useAuth'
import { config, KNOWN_SOURCES } from '../config/environment'
import { RosSidebar } from './RosSidebar'
import { RosHeader } from './RosHeader'
import { ArtifactProtectControl } from './ArtifactProtectControl'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar'
import BugReportIcon from '@mui/icons-material/BugReport'
import DownloadIcon from '@mui/icons-material/Download'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import CodeIcon from '@mui/icons-material/Code'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

// One published version of an artifact — enough to re-open it read-only via openArtifactVersion.
interface PanelArtifactVersion {
  type: string
  version: number
  title: string
  sessionId: string
  bodyR2Key: string
  publishedAt: string
}

// A published artifact for the current session (one per type), with its full version history.
// Drives the panel's artifact-picker + version-picker switcher.
interface SessionArtifact {
  id: string
  type: string
  title: string
  sessionId: string
  latestVersion: number
  isProtected: boolean
  versions: PanelArtifactVersion[]
}

// Most recent publish time across an artifact's versions (ISO strings sort lexicographically).
function latestPublishedAt(a: SessionArtifact): string {
  return a.versions.reduce((max, v) => (v.publishedAt > max ? v.publishedAt : max), '')
}

// Friendly label for the artifact picker — title plus a short type tag to disambiguate types.
function artifactLabel(a: SessionArtifact): string {
  const tag = a.type === 'crosstabs' ? 'Crosstabs' : a.type === 'website' ? 'Website' : a.type === 'survey' ? 'Survey' : a.type
  return `${a.title} · ${tag}`
}

// Map a published-artifact type to the session.panelType used by the draft-banner endpoints.
function artifactTypeToPanelType(type: string): string | undefined {
  return type === 'crosstabs' ? 'crosstabs'
    : type === 'website' ? 'site_builder'
    : type === 'survey' ? 'survey_review'
    : undefined
}

export function RosApp() {
  const auth = useAuth()
  const [devMode, setDevMode] = useState(false)

  // ── Feature flags (PostHog) ───────────────────────────────────────
  // Same flag that gated the Agent Builder on old Studio Ros.
  const showAgents = useFeatureFlag('reachy-agent-builder')
  const { identifyUser } = usePostHog()
  useEffect(() => {
    if (auth.ready && auth.email) identifyUser(auth.email)
  }, [auth.ready, auth.email, identifyUser])
  const [agentsOpen, setAgentsOpen] = useState(false)

  // ── Chat ──────────────────────────────────────────────────────────
  const chatRef = useRef<R3ChatHandle>(null)

  const chatService = useMemo(() => {
    if (!auth.ready) return null
    return new WebChatService(config.API_CF_URL, auth.email ?? undefined, auth.token ?? undefined)
  }, [auth.ready, auth.email, auth.token])

  // ── Sessions (server-side paginated) ──────────────────────────────
  const SESSIONS_PAGE = 200
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [sessionsHasMore, setSessionsHasMore] = useState(false)
  const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false)
  const [liveSessionId, setLiveSessionId] = useState<string | undefined>()

  const fetchSessionsPage = useCallback(async (offset: number) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    const res = await fetch(`${config.API_CF_URL}/api/r3/sessions?limit=${SESSIONS_PAGE}&offset=${offset}`, {
      credentials: 'include', headers,
    })
    if (!res.ok) throw new Error(`sessions ${res.status}`)
    return res.json() as Promise<{ sessions: SessionSummary[]; hasMore?: boolean }>
  }, [auth.token])

  // Load the first (most-recent) page.
  const loadSessions = useCallback(async () => {
    try {
      const data = await fetchSessionsPage(0)
      setSessions(data.sessions || [])
      setSessionsHasMore(!!data.hasMore)
    } catch {
      // Session loading is best-effort
    } finally {
      setSessionsLoading(false)
    }
  }, [fetchSessionsPage])

  // Append the next page (older sessions).
  const loadMoreSessions = useCallback(async () => {
    if (sessionsLoadingMore || !sessionsHasMore) return
    setSessionsLoadingMore(true)
    try {
      const data = await fetchSessionsPage(sessions.length)
      setSessions(prev => [...prev, ...(data.sessions || [])])
      setSessionsHasMore(!!data.hasMore)
    } catch {
      // best-effort
    } finally {
      setSessionsLoadingMore(false)
    }
  }, [fetchSessionsPage, sessions.length, sessionsHasMore, sessionsLoadingMore])

  useEffect(() => {
    if (auth.ready) loadSessions()
  }, [auth.ready, loadSessions])

  // ── Session sidebar (right-side colored thread) ───────────────────
  // Full-page sessions overlay ("See all" history) — replaces the old right-edge thread sidebar.
  const [historyOpen, setHistoryOpen] = useState(false)
  // Unpublished-changes banner: set on session resume when the agent's draft is ahead of the
  // last published version (draft_meta.dirty).
  const [draftBanner, setDraftBanner] = useState<{ type: string; baseVersion: number | null } | null>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const handleSelectSession = useCallback(async (sessionId: string) => {
    if (!chatRef.current) return
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`

      const res = await fetch(`${config.API_CF_URL}/api/r3/sessions/${sessionId}/messages`, {
        credentials: 'include',
        headers,
      })
      if (!res.ok) return
      const data = await res.json() as { messages: Array<{ role: 'user' | 'assistant'; content: string; agent?: string }> }
      chatRef.current.loadSession(sessionId, data.messages || [])
      setLiveSessionId(sessionId)
    } catch {
      // Session resume failed
    }
  }, [auth.token])

  const handleNewChat = useCallback(() => {
    chatRef.current?.reset()
    setLiveSessionId(undefined)
    setMcpPanelData(null)
    setMcpPanelOpen(false)
    setViewingVersion(null)
    setSelectedArtifactId(null)
    setSelectedVersion(null)
    setPublishing(false)
    setSessionArtifacts([])
    setDraftBanner(null)
  }, [])

  // ── MCP App panel ─────────────────────────────────────────────────
  const [mcpPanelData, setMcpPanelData] = useState<McpAppData | null>(null)
  const [mcpPanelOpen, setMcpPanelOpen] = useState(false)
  const mcpContainerRef = useRef<McpAppContainerHandle>(null)

  const handleMcpApp = useCallback((data: McpAppData) => {
    setMcpPanelData(data)
    setMcpPanelOpen(true)
  }, [])

  // ── Site builder panel ────────────────────────────────────────────
  const [siteArtifacts, setSiteArtifacts] = useState<SiteArtifacts | null>(null)
  const [siteBuilderOpen, setSiteBuilderOpen] = useState(false)

  // All published artifacts for the live session (≤1 per type), each with its version history.
  // Drives the panel switcher: artifact-picker (across types) + version-picker (within an artifact).
  // Sourced from /api/r3/artifacts, narrowed to this session.
  const [sessionArtifacts, setSessionArtifacts] = useState<SessionArtifact[]>([])
  // Which artifact + version the panel is currently showing. selectedArtifactId also keys the
  // password-protect control (browser→worker POST against this id, NEVER through chat/agent/LLM).
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  // Publishing-in-progress: set when the user clicks the header Publish, cleared when the chat turn
  // that runs the publish tool completes. Drives the button busy label + the panel overlay.
  const [publishing, setPublishing] = useState(false)
  // Mirror in a ref so the turn-end callback can read "were we publishing?" without a state-updater
  // side effect (to refresh artifacts after a successful publish).
  const publishingRef = useRef(false)
  useEffect(() => { publishingRef.current = publishing }, [publishing])

  const handleSiteBuilderPlan = useCallback((data: SiteBuilderPlanData) => {
    setSiteArtifacts(prev => ({
      siteId: data.siteId,
      agentState: prev?.agentState ?? '',
      sitePlan: data.sections,
      htmlContent: prev?.htmlContent ?? null,
      clientSlug: prev?.clientSlug,
      publishedUrl: prev?.publishedUrl,
    }))
    setSiteBuilderOpen(true)
  }, [])

  const handleSiteBuilderGenerating = useCallback((siteId: number) => {
    setSiteBuilderOpen(true)
    setSiteArtifacts(prev => prev ? { ...prev, siteId } : { siteId, agentState: '', sitePlan: null, htmlContent: null })
  }, [])

  const handleSiteBuilderCodeChunk = useCallback((_data: SiteBuilderCodeChunkData) => {
    setSiteBuilderOpen(true)
  }, [])

  const handleSiteBuilderPreview = useCallback((html: string, siteId: number) => {
    setSiteArtifacts(prev => ({
      siteId,
      agentState: prev?.agentState ?? '',
      sitePlan: prev?.sitePlan ?? null,
      htmlContent: html,
      clientSlug: prev?.clientSlug,
      publishedUrl: prev?.publishedUrl,
    }))
    setSiteBuilderOpen(true)
  }, [])

  const handleSiteBuilderPublished = useCallback((url: string, siteId: number) => {
    setSiteArtifacts(prev => prev ? { ...prev, siteId, publishedUrl: url } : prev)
  }, [])

  // ── Right panel resize ────────────────────────────────────────────
  const [panelWidthPct, setPanelWidthPct] = useState(55)
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  // ── Panel view controls — fullscreen (all panels) + device preview (site builder) ──
  const [panelFullscreen, setPanelFullscreen] = useState(false)
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [siteView, setSiteView] = useState<'preview' | 'code'>('preview')

  // Open the rendered site HTML in a new browser tab (matches the original reachy-host).
  const openSiteInNewTab = useCallback(() => {
    const html = siteArtifacts?.htmlContent
    if (!html) return
    const win = window.open('', '_blank') as unknown as Window | null
    if (win) { win.document.write(html); win.document.close() }
  }, [siteArtifacts])

  const downloadSiteHtml = useCallback(() => {
    const html = siteArtifacts?.htmlContent
    if (!html) return
    const slug = siteArtifacts?.publishedUrl
      ? new URL(siteArtifacts.publishedUrl).hostname.split('.')[0]
      : 'deliverable'
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug}.html`
    a.click()
    URL.revokeObjectURL(url)
  }, [siteArtifacts])

  const handleDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    isDraggingRef.current = true
    setIsDragging(true)

    const onMove = (ev: PointerEvent) => {
      if (!isDraggingRef.current) return
      const pct = 100 - (ev.clientX / window.innerWidth) * 100
      setPanelWidthPct(Math.max(25, Math.min(75, pct)))
    }
    const onUp = () => {
      isDraggingRef.current = false
      setIsDragging(false)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  // ── MCP App resume on session select ──────────────────────────────
  // Each resume probe returns true if it restored a panel for this session, so the caller can
  // try them in priority order and stop at the first hit (a session belongs to one agent).
  const resourceUri = config.MCP_APP_URL || 'https://reachy-mcp-app.pages.dev'

  const handleSessionResumeMcp = useCallback(async (sessionId: string): Promise<boolean> => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`

      const res = await fetch(`${config.API_CF_URL}/api/mcp/tool`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ tool: 'show_survey_review', sessionId }),
      })
      if (!res.ok) return false
      const result = await res.json() as { structuredContent: Record<string, unknown> }
      const sc = result.structuredContent ?? {}
      if ((sc as Record<string, unknown[]>).nodes?.length > 0) {
        setMcpPanelData({
          serverId: 'authoring',
          resourceUri,
          toolName: 'show_survey_review',
          toolInput: {},
          toolResult: { content: [{ type: 'text', text: 'Restored' }], structuredContent: sc },
        })
        setMcpPanelOpen(true)
        return true
      }
    } catch {
      // MCP resume is best-effort
    }
    return false
  }, [auth.token, resourceUri])

  // Crosstabs resume: a session has a restorable crosstabs panel once it has a saved
  // classification (or anything further). Restore the workspace at its furthest-advanced tab.
  const handleSessionResumeCrosstabs = useCallback(async (sessionId: string): Promise<boolean> => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`

      const res = await fetch(`${config.API_CF_URL}/api/crosstabs/tool`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ tool: 'get_state', sessionId, arguments: {} }),
      })
      if (!res.ok) return false
      const result = await res.json() as { structuredContent?: Record<string, unknown> }
      const sc = result.structuredContent ?? {}
      // No classification yet → nothing meaningful to show.
      if (!sc.classification) return false
      // Open at the furthest-advanced tab. If a completed output exists, open PREVIEW (the finished
      // deliverable) — not Plan. Mirrors the panel's own defaultTab ordering.
      const jobComplete = (sc.latestJob as { status?: string } | null)?.status === 'complete'
      const activeTab = jobComplete ? 'preview'
        : sc.plan ? 'plan'
        : sc.objectives ? 'objectives'
        : 'variables'
      setMcpPanelData({
        serverId: 'crosstabs',
        resourceUri,
        toolName: 'show_crosstabs_review',
        toolInput: { tab: activeTab },
        toolResult: { content: [{ type: 'text', text: 'Restored' }], structuredContent: { ...sc, activeTab } },
      })
      setMcpPanelOpen(true)
      return true
    } catch {
      // Crosstabs resume is best-effort
    }
    return false
  }, [auth.token, resourceUri])

  // ── Site builder resume ────────────────────────────────────────────
  const handleSessionResumeSiteBuilder = useCallback(async (sessionId: string): Promise<boolean> => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`

      const res = await fetch(`${config.API_CF_URL}/api/site-builder/tool`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ tool: 'get_site', arguments: {}, sessionId }),
      })
      if (!res.ok) return false
      const data = await res.json() as { structuredContent?: Record<string, unknown> }
      const sc = data.structuredContent
      if (!sc) return false

      // renderHtml carries signed asset URLs (raw htmlContent has unresolved
      // asset:// refs that 404 in the preview iframe).
      const htmlContent = (sc.renderHtml as string | null) ?? (sc.htmlContent as string | null)
      const sitePlan = sc.sitePlan as SiteBuilderPlanData['sections'] | null
      if (htmlContent || sitePlan) {
        setSiteArtifacts({
          siteId: (sc.siteId as number) || 0,
          agentState: (sc.agentState as string) || '',
          sitePlan: sitePlan || null,
          htmlContent: htmlContent || null,
          publishedUrl: (sc.publishedUrl as string) || undefined,
        })
        setSiteBuilderOpen(true)
        return true
      }
    } catch {
      // Site builder resume is best-effort
    }
    return false
  }, [auth.token])

  // When switching sessions: ALWAYS reset every panel first so the previous chat's panel can't
  // linger and stale state from memory can't bleed across chats. Then restore the ONE panel this
  // session has, keyed on panelType — the same signal that drives the history marker, so the tag
  // and the panel-on-open are always consistent. (Falls back to probing if panelType is absent,
  // e.g. a pre-panel_type session that hasn't been re-touched.)
  // After resume, surface the draft/publish state: if the agent's working draft has unpublished
  // changes since the last published version, show the "Unpublished changes" banner.
  // The agent's draft_meta for a session+type: does an unpublished draft exist (dirty), and which
  // version does it descend from. Used for both the "Unpublished changes" banner and the editability
  // routing (latest version is editable only when NO draft exists). panelType maps to the Facet route.
  const fetchDraftMeta = useCallback(async (sessionId: string, panelType?: string): Promise<{ artifactId?: string; baseVersion?: number; dirty?: boolean } | null> => {
    const endpoint = panelType === 'crosstabs' ? '/api/crosstabs/tool'
      : panelType === 'site_builder' ? '/api/site-builder/tool'
      : panelType === 'survey_review' ? '/api/mcp/tool' : null
    if (!endpoint) return null
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      const res = await fetch(`${config.API_CF_URL}${endpoint}`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ tool: 'get_draft_meta', sessionId, arguments: {} }),
      })
      if (!res.ok) return null
      return ((await res.json()) as { structuredContent?: { artifactId?: string; baseVersion?: number; dirty?: boolean } }).structuredContent ?? null
    } catch {
      return null
    }
  }, [auth.token])

  const refreshDraftBanner = useCallback(async (sessionId: string, panelType?: string) => {
    setDraftBanner(null)
    const meta = await fetchDraftMeta(sessionId, panelType)
    const dirty = !!(meta?.dirty && meta.artifactId)
    if (dirty) setDraftBanner({ type: panelType!, baseVersion: meta!.baseVersion ?? null })
  }, [fetchDraftMeta])

  // Open a SPECIFIC published artifact version (clicked in the Artifacts sidebar) read-only:
  // crosstabs → output view over the frozen table set; website → frozen html; survey → review
  // of the frozen nodes. The panel header shows the version badge.
  const [viewingVersion, setViewingVersion] = useState<string | null>(null)
  const openArtifactVersion = useCallback(async (v: { id: string; type: string; version: number; title: string; sessionId: string; bodyR2Key: string }) => {
    try {
      const headers: Record<string, string> = {}
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      const res = await fetch(`${config.API_CF_URL}/api/r3/artifact-body?key=${encodeURIComponent(v.bodyR2Key)}`, { credentials: 'include', headers })
      if (!res.ok) return
      const body = await res.json() as Record<string, any>
      setDraftBanner(null)
      setViewingVersion(`${v.title} · v${v.version}`)
      setSelectedArtifactId(v.id)   // which artifact the switcher is on
      setSelectedVersion(v.version) // which version within it
      setLiveSessionId(v.sessionId) // panel tool calls run in the owning session's context
      if (v.type === 'website') {
        setMcpPanelData(null)
        setSiteArtifacts({ siteId: 0, agentState: '', sitePlan: body.sitePlan ?? null, htmlContent: body.html ?? null, publishedUrl: body.publishedUrl ?? undefined })
        setSiteBuilderOpen(true)
        setMcpPanelOpen(true)
        return
      }
      setSiteArtifacts(null); setSiteBuilderOpen(false)
      if (v.type === 'survey') {
        setMcpPanelData({
          serverId: 'authoring', resourceUri,
          toolName: 'show_survey_review', toolInput: {},
          toolResult: { content: [{ type: 'text', text: 'Published version' }], structuredContent: { ...body, studyName: `${v.title} (v${v.version})` } },
        })
        setMcpPanelOpen(true)
        return
      }
      // crosstabs: frozen output view (the version's table set + its Excel)
      setMcpPanelData({
        serverId: 'crosstabs', resourceUri,
        toolName: 'show_crosstabs_review', toolInput: { tab: 'preview' },
        toolResult: { content: [{ type: 'text', text: 'Published version' }], structuredContent: {
          activeTab: 'preview',
          frozenOutputKey: body.outputR2Key, frozenExcelKey: body.excelR2Key ?? null,
          manifest: body.manifest ?? [], snapshot: body.snapshot ?? null,
        } },
      })
      setMcpPanelOpen(true)
    } catch { /* best-effort */ }
  }, [auth.token, resourceUri])

  // Switch the panel back to the live editable DRAFT (the dropdown's "Draft" choice). Reuses the
  // same resume handler the session-open path uses — it restores the working draft (full tabs).
  const openDraft = useCallback(async (type: string, artifactId: string | null) => {
    setViewingVersion(null)
    setSelectedVersion(null)           // null = Draft is the active view
    setSelectedArtifactId(artifactId)  // keep the artifact picker in sync
    if (!liveSessionId) return
    if (type === 'crosstabs') await handleSessionResumeCrosstabs(liveSessionId)
    else if (type === 'survey') await handleSessionResumeMcp(liveSessionId)
    else if (type === 'website') await handleSessionResumeSiteBuilder(liveSessionId)
    void refreshDraftBanner(liveSessionId, artifactTypeToPanelType(type))
  }, [liveSessionId, handleSessionResumeCrosstabs, handleSessionResumeMcp, handleSessionResumeSiteBuilder, refreshDraftBanner])

  // Selecting ANY published version vN opens it READ-ONLY (frozen view) — published versions are
  // immutable snapshots, full stop. The ONLY editable thing is "Draft" (handled by openDraft). This
  // is the whole model: Draft = editable working state; v1..vN = read-only history. No special-casing
  // of the latest version.
  const selectVersion = useCallback(async (artifact: SessionArtifact, version: PanelArtifactVersion) => {
    await openArtifactVersion({ ...version, id: artifact.id })
  }, [openArtifactVersion])

  // Reset the working draft to a clean copy of the latest published version — wipes the draft's
  // unpublished edits server-side, then reopens the editable Draft. Confirmed first (destructive).
  const resetDraftToLatest = useCallback(async (artifact: SessionArtifact) => {
    if (!window.confirm('Reset the draft to the latest published version? Your unpublished changes will be lost.')) return
    const endpoint = artifact.type === 'crosstabs' ? '/api/crosstabs/tool'
      : artifact.type === 'website' ? '/api/site-builder/tool'
      : artifact.type === 'survey' ? '/api/mcp/tool' : null
    if (!endpoint) return
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      await fetch(`${config.API_CF_URL}${endpoint}`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ tool: 'discard_draft', sessionId: artifact.sessionId, arguments: {} }),
      })
    } catch { /* best-effort; we still refresh the view below */ }
    setDraftBanner(null)
    // Reopen the editable Draft — now reset to a clean copy of the latest published version.
    await openDraft(artifact.type, artifact.id)
  }, [auth.token, openDraft])

  // Load ALL published artifacts for the live session so the panel switcher can offer both an
  // artifact picker (across types) and a version picker (within an artifact). Reuses the same
  // /api/r3/artifacts catalog the sidebar reads; we narrow to THIS session. Clears when no session.
  const loadSessionArtifacts = useCallback(async (sessionId: string): Promise<SessionArtifact[]> => {
    const headers: Record<string, string> = {}
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    try {
      const res = await fetch(`${config.API_CF_URL}/api/r3/artifacts`, { credentials: 'include', headers })
      if (!res.ok) return []
      const data = await res.json() as { groups?: Array<{ items: SessionArtifact[] }> }
      const mine = (data.groups ?? []).flatMap(g => g.items).filter(it => it.sessionId === sessionId)
      return mine
    } catch {
      return []
    }
  }, [auth.token])

  useEffect(() => {
    const panelOpen = mcpPanelOpen || siteBuilderOpen
    if (!panelOpen || !liveSessionId) { setSessionArtifacts([]); return }
    let cancelled = false
    loadSessionArtifacts(liveSessionId).then(arts => { if (!cancelled) setSessionArtifacts(arts) })
    return () => { cancelled = true }
  }, [mcpPanelOpen, siteBuilderOpen, liveSessionId, loadSessionArtifacts])

  const handleSessionSelect = useCallback(async (sessionId: string) => {
    setMcpPanelData(null)
    setMcpPanelOpen(false)
    setSiteArtifacts(null)
    setSiteBuilderOpen(false)
    setDraftBanner(null)
    setViewingVersion(null)
    setSelectedArtifactId(null)
    setSelectedVersion(null)
    setPublishing(false)

    await handleSelectSession(sessionId)

    // Resume always opens the LIVE working DRAFT (editable) — never a frozen published version by
    // default. A draft stays a draft no matter how many edits; publishing is what mints a version,
    // and further edits create a new draft on top. The panel's version switcher is how the user
    // views a specific published version read-only. We pick WHICH type's draft to open by the
    // newest published artifact (max published_at); if nothing's published, the session's panelType.
    const artifacts = await loadSessionArtifacts(sessionId)
    setSessionArtifacts(artifacts)
    const newest = artifacts.length
      ? artifacts.reduce((a, b) => (latestPublishedAt(b) > latestPublishedAt(a) ? b : a))
      : null
    // Select the newest artifact (so its version dropdown shows) but keep selectedVersion null —
    // the panel opens on the live Draft, not a frozen version.
    setSelectedArtifactId(newest?.id ?? null)
    const newestType = newest
      ? artifactTypeToPanelType(newest.type)
      : sessions.find(s => s.id === sessionId)?.panelType

    switch (newestType) {
      case 'crosstabs': await handleSessionResumeCrosstabs(sessionId); void refreshDraftBanner(sessionId, newestType); return
      case 'survey_review': await handleSessionResumeMcp(sessionId); void refreshDraftBanner(sessionId, newestType); return
      case 'site_builder': await handleSessionResumeSiteBuilder(sessionId); void refreshDraftBanner(sessionId, newestType); return
    }
    // Unknown/absent type — probe in priority order, first hit wins.
    if (await handleSessionResumeCrosstabs(sessionId)) { void refreshDraftBanner(sessionId, 'crosstabs'); return }
    if (await handleSessionResumeMcp(sessionId)) { void refreshDraftBanner(sessionId, 'survey_review'); return }
    if (await handleSessionResumeSiteBuilder(sessionId)) { void refreshDraftBanner(sessionId, 'site_builder') }
  }, [sessions, handleSelectSession, loadSessionArtifacts, handleSessionResumeCrosstabs, handleSessionResumeMcp, handleSessionResumeSiteBuilder, refreshDraftBanner])

  // ── File upload ───────────────────────────────────────────────────
  const uploadFile = useCallback(async (sessionId: string, file: File): Promise<string> => {
    const formData = new FormData()
    formData.append('file', file)

    const headers: Record<string, string> = {}
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`

    const res = await fetch(
      `${config.API_CF_URL}/api/upload?sessionId=${encodeURIComponent(sessionId)}&fileName=${encodeURIComponent(file.name)}`,
      { method: 'POST', headers, body: file, credentials: 'include' },
    )
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
    const data = await res.json() as { key: string }
    return data.key
  }, [auth.token])

  // ── Source context (back link) ────────────────────────────────────
  const source = new URLSearchParams(window.location.search).get('source')
  const sourceInfo = source ? KNOWN_SOURCES[source] : null

  // ── Session created callback ──────────────────────────────────────
  // Voice input: fetch a short-lived AssemblyAI streaming token from reachy-cf. The shared chat's
  // useVoiceInput opens the AssemblyAI WebSocket with it directly; the API key stays in the worker.
  const fetchVoiceToken = useCallback(async (): Promise<string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    const res = await fetch(`${config.API_CF_URL}/api/r3/transcribe/token`, {
      method: 'POST', credentials: 'include', headers,
    })
    if (!res.ok) throw new Error('Failed to get voice token')
    const data = await res.json() as { token: string }
    return data.token
  }, [auth.token])

  const handleSessionCreated = useCallback((sessionId: string) => {
    setLiveSessionId(sessionId)
    // Refresh session list after a short delay
    setTimeout(loadSessions, 1000)
  }, [loadSessions])

  // ── Render ────────────────────────────────────────────────────────

  if (!auth.ready) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#faf9f5' }}>
        <CircularProgress sx={{ color: '#6b8f5e' }} />
      </Box>
    )
  }

  if (auth.error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#faf9f5', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5" sx={{ color: '#4a6b3f', fontFamily: "'Source Serif 4', serif" }}>Rival OS</Typography>
        <Typography sx={{ color: '#c45048', fontSize: '0.9rem' }}>{auth.error}</Typography>
      </Box>
    )
  }

  if (!chatService) return null

  const activePanelType = mcpPanelOpen && mcpPanelData ? 'mcp_app'
    : siteBuilderOpen && siteArtifacts ? 'site_builder'
    : null
  const rightPanelOpen = activePanelType !== null
  const chatWidthPct = rightPanelOpen ? 100 - panelWidthPct : 100

  // Switcher derivations: the selected artifact (or fall back to the first), its versions, and its
  // protection state. The artifact dropdown lists all of the session's artifacts (one per type).
  const selectedArtifact = sessionArtifacts.find(a => a.id === selectedArtifactId) ?? null
  const panelVersions = selectedArtifact?.versions ?? []
  const panelArtifactProtected = !!selectedArtifact?.isProtected

  // Toggle must reopen whichever panel has data — flipping only mcpPanelOpen
  // left site-builder panels (no mcpPanelData) impossible to reopen after close.
  const togglePanel = () => {
    if (rightPanelOpen) {
      setMcpPanelOpen(false); setSiteBuilderOpen(false); setPanelFullscreen(false)
    } else if (mcpPanelData) {
      setMcpPanelOpen(true)
    } else if (siteArtifacts) {
      setSiteBuilderOpen(true)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: '#faf9f5' }}>
      {/* Header — full width, top */}
      <RosHeader
        sourceInfo={sourceInfo}
        email={auth.email}
        name={auth.name}
        onNewChat={handleNewChat}
        mcpPanelOpen={!!rightPanelOpen}
        onToggleMcpPanel={togglePanel}
        devMode={devMode}
        onToggleDevMode={() => setDevMode(!devMode)}
        sessionId={liveSessionId}
        token={auth.token}
        tags={sessions.find(s => s.id === liveSessionId)?.tags ?? {}}
        onTagsChange={(tags) => setSessions(prev => prev.map(s => s.id === liveSessionId ? { ...s, tags } : s))}
      />

      {/* Below header: sidebar + chat */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left sidebar */}
        <RosSidebar
          sessions={sessions}
          sessionsLoading={sessionsLoading}
          activeSessionId={liveSessionId}
          onSelectSession={handleSessionSelect}
          onNewChat={handleNewChat}
          onOpenHistory={() => setHistoryOpen(true)}
          onOpenArtifactVersion={openArtifactVersion}
          apiBaseUrl={config.API_CF_URL}
          token={auth.token}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          showAgents={showAgents}
          onOpenAgents={() => setAgentsOpen(true)}
        />

        {/* Chat + Right panel */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Chat column */}
          <Box sx={{
            width: `${chatWidthPct}%`,
            transition: isDragging ? 'none' : 'width 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Unpublished-changes banner — the working draft is ahead of the last published version */}
            {draftBanner && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75,
                background: '#fdf6e8', borderBottom: '1px solid #ecd9ae',
              }}>
                <Typography sx={{ fontSize: '0.78rem', color: '#8a6d1f', fontWeight: 500 }}>
                  Unpublished changes since v{draftBanner.baseVersion ?? '?'} — publish to create v{(draftBanner.baseVersion ?? 0) + 1}.
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography
                  onClick={() => {
                    const noun = draftBanner.type === 'crosstabs' ? 'crosstabs deliverable' : draftBanner.type === 'site_builder' ? 'website' : 'survey'
                    chatRef.current?.sendMessage(`Publish the ${noun} — I approve the current draft.`)
                    setDraftBanner(null)
                  }}
                  sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#8a6d1f', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  Publish
                </Typography>
                <Typography onClick={() => setDraftBanner(null)} sx={{ fontSize: '0.85rem', color: '#b8a468', cursor: 'pointer' }}>✕</Typography>
              </Box>
            )}
            <R3Chat
              ref={chatRef}
              chatService={chatService}
              onSessionCreated={handleSessionCreated}
              fetchVoiceToken={fetchVoiceToken}
              headerActions={
                <>
                  <Tooltip title="Export"><IconButton size="small" sx={{ color: '#999', '&:hover': { color: '#6b8f5e' } }}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title={rightPanelOpen ? 'Close panel' : 'Open panel'}><IconButton size="small" onClick={togglePanel} sx={{ color: rightPanelOpen ? '#6b8f5e' : '#999' }}><ViewSidebarIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title={devMode ? 'Dev mode ON — click to switch to clean mode' : 'Click to enable dev mode'}><IconButton size="small" onClick={() => setDevMode(!devMode)} sx={{ color: devMode ? '#c4873b' : '#ccc' }}><BugReportIcon fontSize="small" /></IconButton></Tooltip>
                </>
              }
              onStreamingAssistantContent={(content) => {
                // content === null marks turn end (or error/reset). Clear the publishing UX, and if a
                // publish just ran, refresh the session's artifacts so the new version (and the updated
                // "Draft (from vN)") appear in the switcher without a page reload.
                if (content === null) {
                  if (publishingRef.current && liveSessionId) {
                    loadSessionArtifacts(liveSessionId).then(setSessionArtifacts)
                  }
                  setPublishing(false)
                }
              }}
              onMcpApp={handleMcpApp}
              onSiteBuilderPlan={handleSiteBuilderPlan}
              onSiteBuilderGenerating={handleSiteBuilderGenerating}
              onSiteBuilderCodeChunk={handleSiteBuilderCodeChunk}
              onSiteBuilderPreview={handleSiteBuilderPreview}
              onSiteBuilderPublished={handleSiteBuilderPublished}
              apiBaseUrl={config.API_CF_URL}
              sessionId={liveSessionId}
              devMode={devMode}
              uploadFile={uploadFile}
            />
          </Box>

          {/* Drag handle */}
          {rightPanelOpen && (
            <Box
              onPointerDown={handleDragStart}
              sx={{
                width: 4, cursor: 'col-resize', background: isDragging ? '#6b8f5e' : '#e8e5de',
                '&:hover': { background: '#6b8f5e' }, transition: 'background 0.15s', flexShrink: 0,
              }}
            />
          )}

          {/* Right panel — MCP App or Site Preview. Fullscreen lifts the whole panel over the
              viewport (works for both panel types); device toggle frames the site preview. */}
          {rightPanelOpen && (
            <Box sx={{
              width: `${panelWidthPct}%`,
              transition: isDragging ? 'none' : 'width 0.2s ease',
              borderLeft: '1px solid #e8e5de',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              ...(panelFullscreen && {
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                width: '100%', zIndex: 1300, borderLeft: 'none', background: '#fff',
              }),
            }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 2, py: 1, borderBottom: '1px solid #e8e5de', background: '#fff',
              }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#4a6b3f', textTransform: 'uppercase', letterSpacing: '0.04em', flex: 1 }}>
                  {activePanelType === 'site_builder' ? 'Site Preview' :
                   mcpPanelData?.toolName === 'show_survey_review' ? 'Survey Review' :
                   mcpPanelData?.serverId === 'crosstabs' ? 'Crosstabs' : 'MCP App'}{viewingVersion ? ` — ${viewingVersion} (published)` : ''}
                </Typography>

                {/* Artifact picker — present when the session has more than one artifact (across
                    types). Picking one opens its live Draft (the default view for any artifact). */}
                {sessionArtifacts.length > 1 && (
                  <select
                    value={selectedArtifactId ?? ''}
                    onChange={e => {
                      const art = sessionArtifacts.find(a => a.id === e.target.value)
                      if (art) openDraft(art.type, art.id)
                    }}
                    style={{
                      fontSize: '0.68rem', color: '#4a4a4a', border: '1px solid #d8d3ca',
                      borderRadius: 6, padding: '2px 6px', background: '#fff', marginRight: 6, cursor: 'pointer', maxWidth: 160,
                    }}
                    title="Switch artifact"
                  >
                    {selectedArtifactId === null && <option value="" disabled>Select artifact</option>}
                    {sessionArtifacts.map(a => (
                      <option key={a.id} value={a.id}>{artifactLabel(a)}</option>
                    ))}
                  </select>
                )}

                {/* Version picker — ALWAYS shown while a deliverable panel is open, so the draft→version
                    model is discoverable from the start. Before any publish it's just "Draft". After
                    publishing it gains v1, v2… Selecting the latest with no draft opens it editable
                    (that's how a draft is born); older versions / any version while a draft exists are
                    read-only. */}
                {(activePanelType === 'site_builder' || mcpPanelData?.serverId === 'crosstabs' || mcpPanelData?.toolName === 'show_survey_review') && (
                  <select
                    value={selectedVersion !== null ? String(selectedVersion) : 'draft'}
                    onChange={e => {
                      if (e.target.value === 'draft') {
                        if (selectedArtifact) openDraft(selectedArtifact.type, selectedArtifact.id)
                        else { setSelectedVersion(null); setViewingVersion(null) } // pre-publish: already on the live draft
                        return
                      }
                      const v = panelVersions.find(pv => pv.version === Number(e.target.value))
                      if (v && selectedArtifact) selectVersion(selectedArtifact, v)
                    }}
                    style={{
                      fontSize: '0.68rem', color: '#4a4a4a', border: '1px solid #d8d3ca',
                      borderRadius: 6, padding: '2px 6px', background: '#fff', marginRight: 6, cursor: 'pointer',
                    }}
                    title="View the draft (editable) or a published version (read-only)"
                  >
                    {/* Draft is ALWAYS present and is the ONLY editable entry; it's a working copy of
                        the latest published version. Every vN is read-only. */}
                    <option value="draft">{selectedArtifact ? `Draft (from v${selectedArtifact.latestVersion})` : 'Draft'}</option>
                    {[...panelVersions].sort((a, b) => b.version - a.version).map(v => (
                      <option key={v.version} value={v.version}>v{v.version}</option>
                    ))}
                  </select>
                )}

                {/* Publish — top-level primary action on an EDITABLE crosstabs draft (the live view,
                    not a frozen version). Sends the publish action to the agent; if there's no
                    completed output yet, the agent replies asking to generate first. */}
                {mcpPanelData?.serverId === 'crosstabs' && activePanelType === 'mcp_app' && selectedVersion === null && (
                  <button
                    type="button"
                    disabled={publishing}
                    onClick={() => {
                      setPublishing(true)
                      chatRef.current?.sendMessage('Publish the crosstabs deliverable — I approve the current draft.')
                    }}
                    title="Publish this crosstabs deliverable"
                    style={{
                      fontSize: '0.68rem', color: '#fff', border: 'none', fontWeight: 600,
                      borderRadius: 6, padding: '4px 12px', marginRight: 6,
                      background: publishing ? '#7fbfca' : '#008FA8',
                      cursor: publishing ? 'default' : 'pointer',
                    }}
                  >
                    {publishing ? 'Publishing…' : 'Publish'}
                  </button>
                )}

                {/* Discard the working draft — shown on the Draft view (resets it to a clean copy of
                    the latest published version). Not shown while viewing a read-only version, and
                    only when there's a published version to revert to. */}
                {selectedArtifact && selectedVersion === null && panelVersions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => resetDraftToLatest(selectedArtifact)}
                    title="Reset the draft to the latest published version"
                    style={{
                      fontSize: '0.68rem', color: '#9a5040', border: '1px solid #e0cdc6',
                      borderRadius: 6, padding: '3px 8px', background: '#fff', marginRight: 6, cursor: 'pointer',
                    }}
                  >
                    Reset to latest
                  </button>
                )}

                {/* Password-protect the selected published deliverable (browser→worker only; never via chat). */}
                {selectedArtifact && (
                  <ArtifactProtectControl
                    artifactId={selectedArtifact.id}
                    initiallyProtected={panelArtifactProtected}
                    apiBaseUrl={config.API_CF_URL}
                    token={auth.token}
                  />
                )}

                {/* Site builder: device toggle + code view + download + open-in-new-tab */}
                {activePanelType === 'site_builder' && siteArtifacts?.htmlContent && (
                  <>
                    <Tooltip title="Desktop preview"><IconButton size="small" onClick={() => { setSiteView('preview'); setPreviewDevice('desktop') }} sx={{ color: siteView === 'preview' && previewDevice === 'desktop' ? '#7c3aed' : '#aaa' }}><DesktopWindowsIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Mobile preview"><IconButton size="small" onClick={() => { setSiteView('preview'); setPreviewDevice('mobile') }} sx={{ color: siteView === 'preview' && previewDevice === 'mobile' ? '#7c3aed' : '#aaa' }}><PhoneIphoneIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="View code"><IconButton size="small" onClick={() => setSiteView(v => v === 'code' ? 'preview' : 'code')} sx={{ color: siteView === 'code' ? '#7c3aed' : '#aaa' }}><CodeIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Download HTML"><IconButton size="small" onClick={downloadSiteHtml} sx={{ color: '#5b7fa6' }}><DownloadIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Open in new tab"><IconButton size="small" onClick={openSiteInNewTab} sx={{ color: '#5b7fa6' }}><OpenInNewIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  </>
                )}
                {siteArtifacts?.publishedUrl && activePanelType === 'site_builder' && (
                  <a href={siteArtifacts.publishedUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.68rem', color: '#5b7fa6', marginRight: 4 }}>
                    Open ↗
                  </a>
                )}

                {/* Fullscreen toggle — all panel types */}
                <Tooltip title={panelFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                  <IconButton size="small" onClick={() => setPanelFullscreen(f => !f)} sx={{ color: '#888' }}>
                    {panelFullscreen ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>

                <Box
                  onClick={() => { setMcpPanelOpen(false); setSiteBuilderOpen(false); setPanelFullscreen(false) }}
                  sx={{ cursor: 'pointer', fontSize: '1.1rem', color: '#999', ml: 0.5, '&:hover': { color: '#555' } }}
                >
                  ✕
                </Box>
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {/* Publishing overlay — calm, non-blocking visual while the publish turn runs. */}
                {publishing && (
                  <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 5,
                    background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(1.5px)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                  }}>
                    <CircularProgress size={28} sx={{ color: '#008FA8' }} />
                    <Typography sx={{ fontSize: '0.82rem', color: '#4a4a4a', fontWeight: 500 }}>Publishing your deliverable…</Typography>
                  </Box>
                )}
                {activePanelType === 'mcp_app' && mcpPanelData && (
                  <McpAppContainer
                    ref={mcpContainerRef}
                    {...mcpPanelData}
                    apiBaseUrl={config.API_CF_URL}
                    sessionId={liveSessionId}
                    panelMode
                    onMessage={(text) => chatRef.current?.sendMessage(text)}
                  />
                )}
                {activePanelType === 'site_builder' && siteArtifacts?.htmlContent && siteView === 'preview' && (
                  <Box sx={{
                    width: '100%', height: '100%', overflow: 'auto',
                    display: 'flex', justifyContent: 'center',
                    background: previewDevice === 'mobile' ? '#eceae3' : 'transparent',
                    py: previewDevice === 'mobile' ? 2 : 0,
                  }}>
                    <iframe
                      srcDoc={siteArtifacts.htmlContent}
                      style={previewDevice === 'mobile'
                        ? { width: 375, height: '100%', maxHeight: 812, border: '6px solid #555', borderRadius: 16, background: '#fff' }
                        : { width: '100%', height: '100%', border: 'none' }}
                      // No allow-same-origin: the previewed HTML can be user-uploaded (import
                      // flow) — with same-origin it would run in the app origin with the session.
                      sandbox="allow-scripts"
                      title="Site Preview"
                    />
                  </Box>
                )}
                {activePanelType === 'site_builder' && siteArtifacts?.htmlContent && siteView === 'code' && (
                  <Box component="pre" sx={{
                    width: '100%', height: '100%', overflow: 'auto', m: 0,
                    p: 2, boxSizing: 'border-box', background: '#1e1e1e', color: '#d4d4d4',
                    fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: '0.72rem',
                    lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  }}>
                    {siteArtifacts.htmlContent}
                  </Box>
                )}
                {activePanelType === 'site_builder' && !siteArtifacts?.htmlContent && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', fontSize: '0.85rem' }}>
                    Building site...
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Full-page sessions overlay — "See all" history with search, date groups, and
            client/research-domain folders (replaces the old right-edge thread sidebar). */}
        <SessionsOverlay
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          sessions={sessions}
          hasMore={sessionsHasMore}
          onLoadMore={loadMoreSessions}
          onSelectSession={handleSessionSelect}
          onOpenArtifactVersion={openArtifactVersion}
          token={auth.token}
        />
        {agentsOpen && showAgents && (
          <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, overflowY: 'auto', background: '#faf9f5' }}>
            <AgentsScreen
              apiBaseUrl={config.API_CF_URL}
              token={auth.token}
              onClose={() => setAgentsOpen(false)}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
